import React, { useRef, useEffect, useContext } from 'react';
import GlobalContext from 'hooks/contexts/GlobalContext';
import {
  SchoolFilters,
  MultiRowSchoolListingResults,
  ErrorPriority,
  ClickPortalResultRowsSectionProps,
} from 'types';
import {
  handleSchoolResultsRequestWithUpdates,
  initImpressionDebugging,
} from 'app-requests/clickPortalSchoolManager';
import {
  CLICK_PORTAL_CARD_LAYOUT,
  CLICK_PORTAL_RESULT_ROWS_DISPLAY,
  SCHOOL_IMPRESSION_TYPES,
  SCHOOL_LISTINGS_USER_ACTION,
} from 'consts';
import { getUserRegionCookie } from 'utils/analyticsHelpers';
import { LogError } from 'utils/logging/LogError';
import { LogInfo } from 'utils/logging';
import { EventType } from 'hooks/custom/UpdateUserProfileRequestType';
import listingFiltersFromQuery from 'utils/listingFiltersFromQuery';
import { transformResultRowsConfigToSchoolFilters } from 'utils/transformResultRowsConfigToSchoolFilters';
import { resolveClickPortalResultRowsListingDisplay } from 'utils/resolveClickPortalResultRowsListingDisplay';
import { resolveClickPortalResultCardLayout } from 'utils/resolveClickPortalResultCardLayout';
import Wysiwyg from 'components/blocks/wysiwyg/Wysiwyg';
import { useProfileUpdater } from 'hooks/custom/useProfileUpdater';
import ClickPortalResultRowsLoader from 'components/sections/clickPortalResultRows/ClickPortalResultRowsLoader';
import DefaultErrorBoundary from 'components/error-boundaries/DefaultErrorBoundary';
import ClickPortalSearchControlsAndResults from 'components/form-wizards/click-portal-search/ClickPortalSearchControlsAndResults';
import cx from 'utils/classnames';
import ClickPortalResultRowsList from './ClickPortalResultRowsList';
import ClickPortalResultRowsNav from './ClickPortalResultRowsNav';

export default function ClickPortalResultRowsSection(
  props: ClickPortalResultRowsSectionProps
): React.JSX.Element {
  const {
    title,
    description,
    resultRowsConfig,
    isCurated,
    showTitleAndDescription = true,
    showSearchHeader = true,
    showSearchControls = false,
    searchControlsPosition = 'sidebar',
    mobileTitle,
    initalResults,
    listingDisplay: listingDisplayProp,
    resultCardLayout,
    navLabel,
    navItems,
  } = props;
  const sectionRefs = useRef<(HTMLDivElement | null)[]>(
    resultRowsConfig?.map(() => null) || []
  );

  const { sendProfileUpdate, setPageViewIdTaxonomyMap } = useProfileUpdater();

  const {
    siteMeta: { schoolCode, serviceGuid, siteType, clickPortalSettings },
    sessionInfo: { userSessionId, pageViewId },
    actions: { updateClickPortalData },
    clickPortal: { resultRows, isLoading },
    windowSize: { currentBreakpoint },
  } = useContext(GlobalContext);

  const isMultiRowSite = Boolean(clickPortalSettings?.isMultiRow);
  const listingDisplay = resolveClickPortalResultRowsListingDisplay(
    listingDisplayProp,
    isMultiRowSite
  );
  const isVerticalList =
    listingDisplay === CLICK_PORTAL_RESULT_ROWS_DISPLAY.VERTICAL_LIST;
  const sectionCardLayout = resolveClickPortalResultCardLayout(
    resultCardLayout,
    clickPortalSettings?.resultCardLayout ?? CLICK_PORTAL_CARD_LAYOUT.RESULT_CARD
  );

  /**
   * This handles the request of school listing and mapping of its impression guids
   */
  async function handleSchoolResultsRequest(
    filters: SchoolFilters[],
    meta: {
      originalUrl: string;
      isCurated?: boolean;
      queryParams?: Record<string, unknown>;
    } = { originalUrl: '' }
  ): Promise<MultiRowSchoolListingResults> {
    return handleSchoolResultsRequestWithUpdates(
      filters,
      siteType,
      userSessionId || '', // Pass userSessionId from context, fallback to empty string
      {
        isPersonalized: Boolean(getUserRegionCookie()),
        originalUrl: meta.originalUrl,
        schoolCode,
        serviceGuid,
        isMultiRow: clickPortalSettings?.isMultiRow,
        isCurated: meta.isCurated ?? isCurated,
        geoLocation: {},
        queryParams: meta.queryParams || {},
        cookies: {},
        // we implemented redis cach so we should never fetch impressions
        impressionType:
          SCHOOL_IMPRESSION_TYPES.CP_REQUEST_IMPRESSIONS_CACHE_KEY,
        userAction: SCHOOL_LISTINGS_USER_ACTION.USER_ON_LOAD,
      },
      (data) => {
        updateClickPortalData(data);
      }
    );
  }

  const sectionTitle =
    currentBreakpoint === 'mobile' && mobileTitle ? mobileTitle : title;
  const siteSearchHeaderDescription =
    currentBreakpoint === 'mobile'
      ? clickPortalSettings.searchHeaderDescription.mobile
      : clickPortalSettings.searchHeaderDescription.desktop;
  // Result rows never render ClickPortalSearchHeader, which is the other
  // place this site copy appears. A blank section description still uses it.
  const sectionDescription =
    description || (isVerticalList ? '' : siteSearchHeaderDescription);

  useEffect(() => {
    if (!isLoading) {
      setPageViewIdTaxonomyMap(/* resetIfNoneSet */ true);
      sendProfileUpdate({
        eventType: EventType.SCHOOL_LISTING_LOADED,
      });
    }
  }, [isLoading]);

  useEffect(() => {
    LogInfo('ClickPortalResultRowsSection: Initializing impression debugging');
    initImpressionDebugging();
  }, []);

  useEffect(() => {
    /** Although userSessionId is not needed because we call for impression after results come back if needed
     * if we wait for sessionId this might improve impression tracking. It seems 15% of users are not having
     * impressions tracked. This might be due to them bouncing before the impression tracking is complete.
     * BUG: [FSIU-200] One imprtant thing to note is there is a bug in the superfetch retry. if session id is not in the getImpression
     * can fail and the retries still wont have the session id. That needs to be fixed before removing this.
     */
    if (!userSessionId || !pageViewId) {
      return;
    }

    try {
      const searchParams = showSearchControls
        ? new URLSearchParams(window.location.search)
        : null;
      const queryFilters = searchParams
        ? listingFiltersFromQuery(searchParams)
        : null;
      const requestRows = queryFilters
        ? [queryFilters]
        : transformResultRowsConfigToSchoolFilters(resultRowsConfig);

      if (!requestRows.length) {
        return;
      }

      handleSchoolResultsRequest(requestRows, {
        originalUrl: searchParams
          ? `${window.location.pathname}${window.location.search}`
          : window.location.pathname,
        isCurated: queryFilters ? false : isCurated,
        queryParams: searchParams
          ? Object.fromEntries(searchParams.entries())
          : {},
      });
    } catch (error) {
      LogError(error as Error, {
        priority: ErrorPriority.ONE,
        description: 'Error fetching click portal result rows from pageSection',
      });
    }
  }, [
    resultRowsConfig,
    showSearchControls,
    isCurated,
    userSessionId,
    pageViewId,
  ]);

  let rowsToRender: MultiRowSchoolListingResults['resultRows'] = [];

  if (!isLoading && resultRows.length > 0) {
    rowsToRender = resultRows;
  } else {
    rowsToRender = initalResults?.resultRows || [];
  }

  let listingBody: React.ReactNode = null;
  if (isVerticalList) {
    listingBody = (
      <DefaultErrorBoundary>
        <ClickPortalSearchControlsAndResults
          showSearchControls={showSearchControls}
          searchControlsPosition={searchControlsPosition}
          showSearchHeader={showSearchHeader}
          cardLayout={sectionCardLayout}
          isVerticalList={isVerticalList}
        />
      </DefaultErrorBoundary>
    );
  } else {
    listingBody = (
      <>
        {rowsToRender.map((resultRow, index) => (
          <ClickPortalResultRowsList
            key={`${resultRow?.title}-${resultRow?.subTitle || index}-${
              resultRow?.schoolImpressionGroupGuid
            }`}
            resultRow={resultRow}
            wpTitle={resultRowsConfig[index]?.title}
            wpSubTitle={resultRowsConfig[index]?.subTitle}
            scrollRef={{ current: sectionRefs.current[index] }}
            rowIndex={index}
            isLoading={isLoading}
          />
        ))}
        <ClickPortalResultRowsLoader isLoading={isLoading} />
        <ClickPortalResultRowsLoader isLoading={isLoading} />
        <ClickPortalResultRowsLoader isLoading={isLoading} />
      </>
    );
  }

  return (
    <section
      className={cx('clickPortalResultRowsSection', {
        'clickPortalResultRowsSection--isMultiRow': !isVerticalList,
        'clickPortalResultRowsSection--isVerticalList': isVerticalList,
      })}
    >
      <div className="clickPortalResultRowsSection__inner">
        {showTitleAndDescription && (sectionTitle || sectionDescription) && (
          <>
            {sectionTitle && (
              <div className="clickPortalResultRowsSection__header">
                <h2 className="clickPortalResultRowsSection__title">
                  {sectionTitle}
                </h2>
              </div>
            )}
            {sectionDescription && (
              <div
                className="clickPortalResultRowsSection__description"
                data-testid="clickPortalResultRowsSection__description"
              >
                <Wysiwyg isClean content={sectionDescription} />
              </div>
            )}
          </>
        )}
        <ClickPortalResultRowsNav label={navLabel} navItems={navItems} />
        {listingBody}
      </div>
    </section>
  );
}
